import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuthStore } from "@/stores/auth";
import { resendVerification } from "@/api/client";

export function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get("returnTo");
  const login = useAuthStore((s) => s.login);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [unverified, setUnverified] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSent, setResendSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setUnverified(false);
    setResendSent(false);
    setLoading(true);
    try {
      await login(email, password);
      setLaunching(true);
      window.setTimeout(() => navigate(returnTo || "/dashboard"), 720);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Login failed";
      if (message === "email_not_verified") {
        setUnverified(true);
      } else {
        setError(message);
      }
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResendLoading(true);
    try {
      await resendVerification(email);
      setResendSent(true);
    } catch {
      setError("Failed to resend verification email");
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <>
    <form onSubmit={handleSubmit} className="space-y-5" aria-busy={loading || launching}>
      <div className="space-y-2">
        <label className="text-sm font-medium text-ink-2">Email</label>
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="input px-3.5 py-2.5 text-sm"
          placeholder="you@example.com"
        />
      </div>
      <div className="space-y-2">
        <label className="text-sm font-medium text-ink-2">Password</label>
        <input
          type="password"
          required
          minLength={8}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="input px-3.5 py-2.5 text-sm"
          placeholder="Min 8 characters"
        />
      </div>
      {error && (
        <p className="text-sm text-bad">{error}</p>
      )}
      {unverified && (
        <div className="rounded-ds-sm border border-warn/30 bg-warn/10 p-3.5 text-sm">
          <p className="font-medium text-warn">Email not verified</p>
          <p className="mt-1 text-warn/80">
            Please check your inbox and click the verification link.
          </p>
          {resendSent ? (
            <p className="mt-2 text-ok">Verification email sent. Check your inbox.</p>
          ) : (
            <button
              type="button"
              onClick={handleResend}
              disabled={resendLoading}
              className="mt-2 text-warn underline transition-colors hover:text-warn/80 disabled:opacity-50"
            >
              {resendLoading ? "Sending..." : "Resend verification email"}
            </button>
          )}
        </div>
      )}
      <button
        type="submit"
        disabled={loading || launching}
        className="login-submit btn btn--accent relative w-full overflow-hidden px-3.5 py-2.5 text-sm shadow-sm"
        data-phase={launching ? "success" : loading ? "loading" : "idle"}
      >
        <span className="login-submit__beam" aria-hidden="true" />
        <span className="login-submit__label">
          {launching ? "Workspace ready" : loading ? "Opening workspace" : "Sign in"}
        </span>
        {(loading || launching) && <span className="login-submit__pulse" aria-hidden="true" />}
      </button>
      <div className="flex items-center justify-between text-sm">
        <Link to="/forgot-password" className="text-ink-3 transition-colors hover:text-ink">
          Forgot password?
        </Link>
        <span className="text-ink-3">
          No account?{" "}
          <Link
            to={returnTo ? `/register?returnTo=${encodeURIComponent(returnTo)}` : "/register"}
            className="text-ink-2 transition-colors hover:text-ink"
          >
            Register
          </Link>
        </span>
      </div>
    </form>

    {launching && (
      <div className="login-launch" role="status" aria-live="polite">
        <div className="login-launch__signal" aria-hidden="true">
          {Array.from({ length: 7 }).map((_, index) => (
            <span key={index} style={{ "--signal-index": index } as React.CSSProperties} />
          ))}
        </div>
        <img src="/trace-logo.svg" alt="" className="login-launch__logo" />
        <p>Signal locked</p>
      </div>
    )}
    </>
  );
}
